import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function ConfirmEmailChange() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, success: false, message: "", email: null, oldEmail: null });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, success: false, message: "Confirmation token missing from the link." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/email-change/confirm", { token });
        setState({
          loading: false,
          success: true,
          email: data.email,
          oldEmail: data.old_email,
          message: "Your email address has been updated. Use the new address next time you sign in.",
        });
      } catch (e) {
        setState({
          loading: false,
          success: false,
          message: e.response?.data?.detail || "Confirmation failed.",
        });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="confirm-email-change-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 sm:p-10">
        <div className="flex flex-col items-center text-center">
          {state.loading ? (
            <>
              <Spinner size={48} className="text-[#002BF6] animate-spin" />
              <h1 className="text-xl font-semibold mt-5">Confirming your new email…</h1>
              <p className="text-sm text-zinc-500 mt-2">Just a second.</p>
            </>
          ) : state.success ? (
            <>
              <div className="rounded-full bg-emerald-50 border border-emerald-200 p-3">
                <CheckCircle size={36} weight="bold" className="text-emerald-600" />
              </div>
              <h1 className="text-2xl font-semibold mt-5" data-testid="confirm-success-title">
                Email updated!
              </h1>
              <p className="text-sm text-zinc-600 mt-3">{state.message}</p>
              {state.email && (
                <div className="mt-4 text-xs font-mono bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2 w-full" data-testid="confirm-email-summary">
                  {state.oldEmail && <div className="text-zinc-400 line-through">{state.oldEmail}</div>}
                  <div className="text-emerald-700 font-semibold">{state.email}</div>
                </div>
              )}
              <Link
                to="/login"
                className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-md bg-[#002BF6] text-white text-sm font-medium hover:bg-[#0021C7] transition-colors"
                data-testid="confirm-goto-login-btn"
              >
                Go to sign in
              </Link>
            </>
          ) : (
            <>
              <div className="rounded-full bg-red-50 border border-red-200 p-3">
                <XCircle size={36} weight="bold" className="text-red-600" />
              </div>
              <h1 className="text-2xl font-semibold mt-5" data-testid="confirm-error-title">
                Confirmation failed
              </h1>
              <p className="text-sm text-zinc-600 mt-3" data-testid="confirm-error-message">{state.message}</p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-md border border-zinc-300 text-zinc-700 text-sm font-medium hover:bg-zinc-50 transition-colors"
              >
                Back to sign in
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
